
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Product, Category } from "@/types/common";
import { isValidProductForNavigation } from "@/utils/productUrlUtils";

export const useProductsData = (searchTerm: string, selectedCategory: string, sortBy: string) => {
  const { data: categories } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      try {
        const { data, error } = await supabase
          .from('categories')
          .select('*')
          .order('name');
        
        if (error) {
          console.error('Error fetching categories:', error);
          return [];
        }
        return data as Category[];
      } catch (err) {
        console.error('Categories query failed:', err);
        return [];
      }
    },
  });

  const { data: products, isLoading } = useQuery({
    queryKey: ['products', searchTerm, selectedCategory, sortBy],
    queryFn: async () => {
      try {
        // Optimized: Select only essential columns for product listings
        let query = supabase
          .from('products')
          .select(`
            id,
            name,
            price,
            compare_at_price,
            image_url,
            url_slug,
            featured,
            categories (
              id,
              name
            )
          `)
          .eq('status', 'active');

        if (searchTerm) {
          query = query.ilike('name', `%${searchTerm}%`);
        }

        if (selectedCategory === 'sale') {
          // Filter for products with sale prices (compare_at_price > price)
          query = query
            .not('compare_at_price', 'is', null)
            .gt('compare_at_price', 0);
        } else if (selectedCategory !== 'all') {
          query = query.eq('category_id', selectedCategory);
        }

        // Apply sorting
        switch (sortBy) {
          case 'price_low':
            query = query.order('price', { ascending: true });
            break;
          case 'price_high':
            query = query.order('price', { ascending: false });
            break;
          case 'name':
          default:
            query = query.order('name', { ascending: true });
            break;
        }
        
        const { data, error } = await query;
        if (error) {
          console.error('Error fetching products:', error);
          return [];
        }
        
        // Filter out products with invalid IDs and map url_slug to slug
        const validProducts = (data as Product[])
          .filter(product => isValidProductForNavigation(product))
          .map(product => ({
            ...product,
            slug: product.url_slug || null // Map url_slug to slug, preserving null values
          }));
        
        return validProducts;
      } catch (err) {
        console.error('Products query failed:', err);
        return [];
      }
    },
  });

  return { products, isLoading, categories };
};
